import React, { useState } from 'react';
import Modal from './Modal';
import { deleteClipboardItem, updateClipboardItem } from '../services/supabaseClient';

const ClipboardHistory = ({ history, onDelete, onUpdate, clipboardId }) => {
    const MAX_CHARACTERS = 5000; // Character limit for editing
    const [modal, setModal] = useState({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });
    const [copiedId, setCopiedId] = useState(null);
    const [editingId, setEditingId] = useState(null);
    const [editingText, setEditingText] = useState('');

    const formatTimestampHKT = (timestamp) => {
        try {
            // If timestamp is just a time string (HH:MM:SS), add today's date
            if (typeof timestamp === 'string' && !timestamp.includes('-') && !timestamp.includes('/')) {
                const now = new Date();
                const dateStr = now.toISOString().split('T')[0];
                const dateTimeStr = `${dateStr}T${timestamp.split(' ')[0]}`;
                return new Date(dateStr).toLocaleString('en-US', {
                    timeZone: 'Asia/Hong_Kong',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                });
            }

            const date = new Date(timestamp);

            // Check if date is valid
            if (isNaN(date.getTime())) {
                return 'Invalid date';
            }

            const options = {
                timeZone: 'Asia/Hong_Kong',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
                hour12: true
            };
            return date.toLocaleString('en-US', options);
        } catch (error) {
            console.error('Error formatting timestamp:', error);
            return 'Invalid date';
        }
    };

    const showModal = (title, message, type = 'info', onConfirm = null) => {
        setModal({ isOpen: true, title, message, type, onConfirm });
    };

    const closeModal = () => {
        setModal({ isOpen: false, title: '', message: '', type: 'info', onConfirm: null });
    };

    const openEdit = (id, text) => {
        setEditingId(id);
        setEditingText(text);
    };

    const handleEditSave = async () => {
        try {
            if (editingText.trim().length === 0) {
                showModal('Error', 'Cannot save empty text', 'info');
                return;
            }
            console.log('Saving item:', { id: editingId, text: editingText });
            const result = await updateClipboardItem(editingId, editingText, clipboardId);
            console.log('Item saved successfully:', result);
            // Update parent state via callback
            if (onUpdate) {
                onUpdate(editingId, editingText);
            }
            setEditingId(null);
            setEditingText('');
        } catch (err) {
            console.error('Error updating item:', err);
            showModal('Error', 'Failed to update item', 'info');
        }
    };

    const handleEditCancel = () => {
        setEditingId(null);
        setEditingText('');
    };

    const handleCopy = (id, text) => {
        navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => {
            setCopiedId(null);
        }, 3000);
    };

    const handleDeleteAll = () => {
        if (history.length === 0) {
            showModal('Info', 'No items to delete', 'info');
            return;
        }
        showModal(
            'Confirm Delete All',
            'Are you sure you want to delete all items? This action cannot be undone.',
            'confirm',
            async () => {
                try {
                    // Delete all items in parallel from database
                    const deletePromises = history.map(item => deleteClipboardItem(item.id));
                    await Promise.all(deletePromises);

                    // Delete all items from UI by calling onDelete for each
                    history.forEach(item => onDelete(item.id));

                    closeModal();
                } catch (err) {
                    console.error('Error deleting items:', err);
                    closeModal();
                    showModal('Error', 'Failed to delete some items', 'info');
                }
            }
        );
    };

    return (
        <div className="history-section">
            <Modal
                isOpen={modal.isOpen}
                title={modal.title}
                message={modal.message}
                type={modal.type}
                onConfirm={modal.onConfirm ? modal.onConfirm : closeModal}
                onCancel={closeModal}
                confirmText={modal.type === 'confirm' ? 'Delete' : 'OK'}
                cancelText="Cancel"
            />
            <div className="history-header">
                <h2 className="history-title">Saved Items</h2>
                {history.length > 0 && (
                    <button className="btn-delete-all" onClick={handleDeleteAll}>
                        Delete All
                    </button>
                )}
            </div>
            {history.length === 0 ? (
                <p className="empty-message">No items saved yet.</p>
            ) : (
                <div className="history-list">
                    {history.map((item) => (
                        <div key={item.id} className="history-item">
                            <div className="history-item-content">
                                {editingId === item.id ? (
                                    <>
                                        <div className="edit-textarea-wrapper">
                                            <textarea
                                                value={editingText}
                                                onChange={(e) => {
                                                    if (e.target.value.length <= MAX_CHARACTERS) {
                                                        setEditingText(e.target.value);
                                                    }
                                                }}
                                                className="history-item-textarea"
                                                maxLength={MAX_CHARACTERS}
                                            />
                                            <div className="edit-char-count">
                                                {editingText.length} / {MAX_CHARACTERS}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <div className="history-item-text">
                                            {item.text.split('\n').map((line, index) => (
                                                <div key={index}>{line || '\u00A0'}</div>
                                            ))}
                                        </div>
                                        <p className="history-item-time">Saved at: {formatTimestampHKT(item.timestamp)}</p>
                                    </>
                                )}
                            </div>
                            <div className="history-item-actions">
                                {editingId === item.id ? (
                                    <>
                                        <button
                                            className="btn-save"
                                            onClick={handleEditSave}
                                        >
                                            Save
                                        </button>
                                        <button
                                            className="btn-cancel"
                                            onClick={handleEditCancel}
                                        >
                                            Cancel
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            className="btn-copy"
                                            onClick={() => handleCopy(item.id, item.text)}
                                        >
                                            {copiedId === item.id ? 'Copied' : 'Copy'}
                                        </button>
                                        <button
                                            className="btn-edit"
                                            onClick={() => openEdit(item.id, item.text)}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className="btn-delete"
                                            onClick={() => onDelete(item.id)}
                                        >
                                            Delete
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default ClipboardHistory;